export const revisionQuality = (completed = true) => {
    return {
        "Approver": {
            "By": "N/A",
            "Completed": completed,
            "Date": "N/A"
        },
        "Checker": {
            "By": "N/A",
            "Completed": completed,
            "Date": "N/A"
        },
        "Compiler": {
            "By": "N/A",
            "Completed": completed,
            "Date": "N/A"
        }
    }
};

/**
 * Sanitizes a document number for use in filenames.
 * Replaces path separators (periods, forward/backslashes) with underscores for Storage compatibility.
 * 
 * Per the Document Templating Storage Contract (issue #3):
 * - Document numbers may contain periods (e.g., "S21.ACC-001") or slashes
 * - These must be replaced to prevent nested Storage paths (db.upload uses ref(dir).child(file.name))
 * - Firebase Storage rules are auth-only on `/**`; a slash in the filename creates unintended path segments
 * - Backslashes also stripped (belt-and-braces for Windows-style paths)
 * - Preserves alphanumeric characters and hyphens
 * 
 * @param {string} number - Document number (e.g., "S21.ACC-001")
 * @returns {string} Sanitized number (e.g., "S21_ACC-001")
 * 
 * @example
 * sanitizeDocumentFilename("S21.ACC-001") // "S21_ACC-001"
 * sanitizeDocumentFilename("D21.TREP-042") // "D21_TREP-042"
 * sanitizeDocumentFilename("S21.ACC/001") // "S21_ACC_001" (slashes also replaced)
 * sanitizeDocumentFilename("S21.ACC\\001") // "S21_ACC_001" (backslashes too)
 */
export const sanitizeDocumentFilename = (number) => {
    if (!number) return 'document';
    return number.replace(/[./\\]/g, '_');
};

/**
 * Generates storage paths for document templating system.
 * Variant-aware for STC vs Military collections.
 * 
 * Per the Document Templating Storage Contract (issue #3, #6, #8):
 * - Template upload directory: Database/DocumentTypes/{typeId}/Revisions/{typeRevisionId}/
 * - Template download path: Database/DocumentTypes/{typeId}/Revisions/{typeRevisionId}/{filename}
 * - Current output (with Revisions/ structure): Database/{prefix}Documents/{docId}/Revisions/{docRevisionId}/{sanitized}_{rev}.docx
 * - Legacy output (flat, pre-#6): Database/{prefix}Documents/{docId}/{sanitized}_{rev}.docx
 * 
 * IMPORTANT: db.upload() takes a directory path (no filename) and uploads with child(file.name).
 * Helpers return uploadDir for upload operations and downloadPath for download operations.
 * 
 * @param {Object} options - Path generation options
 * @param {import("../_variants/index.js").Variant} options.variant - Variant (STC_VARIANT or MILITARY_VARIANT)
 * @param {string} [options.documentTypeId] - DocumentType Firebase ID
 * @param {string} [options.typeRevisionId] - DocumentType revision ID (e.g., "--R1", two dashes)
 * @param {string} [options.templateFilename] - Template filename (for download path)
 * @param {string} [options.documentId] - Document Firebase ID
 * @param {string} [options.docRevisionId] - Document revision ID (e.g., "---R1", three dashes)
 * @param {string} [options.documentNumber] - Document number (e.g., "S21.ACC-001")
 * @param {string} [options.revision] - Document revision string (e.g., "1", "P1")
 * @returns {Object} Object with uploadDir and/or downloadPath strings
 * 
 * @example
 * import { STC_VARIANT } from "../_variants/index.js";
 * 
 * // Template upload directory (for db.upload)
 * documentStoragePaths({
 *   variant: STC_VARIANT,
 *   documentTypeId: "-abc123",
 *   typeRevisionId: "--R1"
 * })
 * // Returns: { templateUploadDir: "Database/DocumentTypes/-abc123/Revisions/--R1" }
 * 
 * @example
 * // Template download path (for db.download)
 * documentStoragePaths({
 *   variant: STC_VARIANT,
 *   documentTypeId: "-abc123",
 *   typeRevisionId: "--R1",
 *   templateFilename: "template.docx"
 * })
 * // Returns: { templateDownloadPath: "Database/DocumentTypes/-abc123/Revisions/--R1/template.docx" }
 * 
 * @example
 * // Generated output with Revisions/ structure (current, post-#6)
 * documentStoragePaths({
 *   variant: STC_VARIANT,
 *   documentId: "-xyz789",
 *   docRevisionId: "---R1",
 *   documentNumber: "S21.ACC-001",
 *   revision: "1"
 * })
 * // Returns: { outputUploadDir: "Database/Documents/-xyz789/Revisions/---R1", outputDownloadPath: "Database/Documents/-xyz789/Revisions/---R1/S21_ACC-001_1.docx" }
 * 
 * @example
 * // Generated output (Military variant)
 * documentStoragePaths({
 *   variant: MILITARY_VARIANT,
 *   documentId: "-xyz789",
 *   docRevisionId: "---R1",
 *   documentNumber: "M21.ACC-001",
 *   revision: "1"
 * })
 * // Returns: { outputUploadDir: "Database/MilitaryDocuments/-xyz789/Revisions/---R1", outputDownloadPath: "Database/MilitaryDocuments/-xyz789/Revisions/---R1/M21_ACC-001_1.docx" }
 */
export const documentStoragePaths = ({
    variant,
    documentTypeId,
    typeRevisionId,
    templateFilename,
    documentId,
    docRevisionId,
    documentNumber,
    revision
}) => {
    if (!variant) {
        console.error("documentStoragePaths: variant is required");
        return {};
    }

    const paths = {};

    // Template storage paths (DocumentTypes - no variant prefix, shared across variants)
    if (documentTypeId && typeRevisionId) {
        const baseDir = `Database/DocumentTypes/${documentTypeId}/Revisions/${typeRevisionId}`;
        paths.templateUploadDir = baseDir;
        if (templateFilename) {
            paths.templateDownloadPath = `${baseDir}/${templateFilename}`;
        }
    }

    // Generated output storage paths (Documents - variant-aware collection)
    if (documentId && documentNumber && revision) {
        const collectionName = `${variant.collectionPrefix}Documents`;
        const sanitizedNumber = sanitizeDocumentFilename(documentNumber);
        const filename = `${sanitizedNumber}_${revision}.docx`;
        
        // Current structure (post-#6): Database/{prefix}Documents/{id}/Revisions/{docRevisionId}/{filename}
        if (docRevisionId) {
            const outputDir = `Database/${collectionName}/${documentId}/Revisions/${docRevisionId}`;
            paths.outputUploadDir = outputDir;
            paths.outputDownloadPath = `${outputDir}/${filename}`;
        }
        
        // Legacy structure (pre-#6): Database/{prefix}Documents/{id}/{filename}
        // Keep for fallback when downloading documents generated before Revisions/ implementation
        const legacyOutputDir = `Database/${collectionName}/${documentId}`;
        paths.legacyOutputUploadDir = legacyOutputDir;
        paths.legacyOutputDownloadPath = `${legacyOutputDir}/${filename}`;
    }

    return paths;
};
